import { useState, useRef, useCallback } from 'react';
import { authFetch, API_URL } from './useAuth';

export function usePipeline(apiKey, selectedStoreId, fetchUsage, fetchWebhookActivity) {
  const [syncStatus, setSyncStatus] = useState('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [previewOnly, setPreviewOnly] = useState(false);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [currentStage, setCurrentStage] = useState(0);
  const [totalStages, setTotalStages] = useState(0);
  const [progressPct, setProgressPct] = useState(0);
  const [lastRunId, setLastRunId] = useState(null);
  const [lastRunDryRun, setLastRunDryRun] = useState(false);
  const [tokenPreview, setTokenPreview] = useState(null);
  const [tokenPreviewOpen, setTokenPreviewOpen] = useState(false);
  // Last finished run (success/failed) — kept until the next run starts so the status steps stay visible
  const [lastRun, setLastRun] = useState(null);
  const eventSourceRef = useRef(null);
  const syncStatusRef = useRef('idle');
  const stagesRef = useRef([]);
  const prNumberRef = useRef(null);

  const finishRun = useCallback((status, error) => {
    setLastRun({ status, stages: stagesRef.current, finishedAt: new Date().toISOString(), prNumber: prNumberRef.current, error: error || null });
  }, []);

  const triggerSync = useCallback(async () => {
    if (!apiKey) return;
    setSyncStatus('syncing'); syncStatusRef.current = 'syncing';
    setSyncMessage('Starting pipeline...');
    setPipelineStages([]); setCurrentStage(0); setProgressPct(0);
    setTokenPreview(null); setTokenPreviewOpen(false);
    setLastRun(null); stagesRef.current = []; prNumberRef.current = null;

    try {
      const res = await authFetch(`${API_URL}/api/pipeline/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview_only: previewOnly, store_id: selectedStoreId || undefined })
      });
      const data = await res.json();

      if (res.status === 402) {
        setSyncStatus('failed'); syncStatusRef.current = 'failed';
        setSyncMessage(data.message || 'Usage limit reached.');
        finishRun('failed', data.message || 'Usage limit reached.');
        return;
      }
      if (!res.ok) {
        setSyncStatus('failed'); syncStatusRef.current = 'failed';
        setSyncMessage(data.message || data.error || 'Sync failed');
        finishRun('failed', data.message || data.error || 'Sync failed');
        return;
      }

      const runId = data.run_id;
      setLastRunId(runId);
      setLastRunDryRun(previewOnly);

      if (runId && runId !== 'unknown') {
        const es = new EventSource(`${API_URL}/api/pipeline/stream/${runId}`);
        eventSourceRef.current = es;

        es.onmessage = (event) => {
          try {
            const evt = JSON.parse(event.data);
            if (evt.status === 'connected') return;
            if (evt.status === 'timeout') {
              setSyncMessage('Pipeline timed out');
              setSyncStatus('failed'); syncStatusRef.current = 'failed';
              finishRun('failed', 'Pipeline timed out');
              es.close();
              return;
            }

            setCurrentStage(evt.stage || 0);
            setTotalStages(evt.total_stages || 6);
            setProgressPct(evt.progress_pct || 0);
            setSyncMessage(evt.message || `Stage ${evt.stage}/${evt.total_stages}`);
            const prMatch = /PR #(\d+)/.exec(evt.message || '');
            if (prMatch) prNumberRef.current = prMatch[1];

            const existingIdx = stagesRef.current.findIndex(s => s.stage === evt.stage);
            if (existingIdx >= 0) {
              const updated = [...stagesRef.current];
              updated[existingIdx] = evt;
              stagesRef.current = updated;
            } else {
              stagesRef.current = [...stagesRef.current, evt];
            }
            setPipelineStages(stagesRef.current);

            if (evt.stage_name === 'Pipeline Complete' && evt.status === 'complete') {
              setSyncStatus('success'); syncStatusRef.current = 'success';
              setSyncMessage(previewOnly ? 'Safe Preview complete' : 'All stages complete — tokens deployed');
              setProgressPct(100);
              finishRun('success');
              es.close();
              if (previewOnly && runId) {
                authFetch(`${API_URL}/api/pipeline/outputs/${runId}`)
                  .then(r => r.ok ? r.json() : null)
                  .then(d => { if (d) setTokenPreview(d.tokens || []); })
                  .catch(err => console.error('Token preview fetch failed:', err));
              }
              // Retry-poll: fetch webhook activity with increasing delays
              // DB insert is now committed before SSE fires, but add safety buffer
              fetchUsage();
              const pollWebhook = async (attempt) => {
                const delays = [3000, 5000, 8000];
                await new Promise(r => setTimeout(r, delays[attempt - 1] || 3000));
                await fetchWebhookActivity();
              };
              pollWebhook(1).then(() => pollWebhook(2)).then(() => pollWebhook(3));
            }

            if (evt.status === 'failed') {
              setSyncStatus('failed'); syncStatusRef.current = 'failed';
              finishRun('failed', evt.message);
              es.close();
            }
          } catch (err) { console.error('SSE message parse error:', err); }
        };

        es.onerror = () => {
          es.close();
          if (syncStatusRef.current === 'syncing') {
            setTimeout(() => {
              if (syncStatusRef.current === 'syncing') {
                setSyncMessage('Pipeline running (live stream ended)');
              }
            }, 1000);
          }
        };
      } else {
        setSyncStatus('success'); syncStatusRef.current = 'success';
        setSyncMessage(data.message || 'Sync started');
        finishRun('success');
        setTimeout(() => { fetchUsage(); fetchWebhookActivity(); }, 3000);
      }
    } catch (err) {
      console.error('Pipeline sync failed:', err);
      setSyncStatus('failed'); syncStatusRef.current = 'failed';
      setSyncMessage('Connection failed.');
      finishRun('failed', 'Connection failed.');
    }
  }, [apiKey, previewOnly, selectedStoreId, fetchUsage, fetchWebhookActivity, finishRun]);

  const resetSync = useCallback(() => {
    setSyncStatus('idle'); syncStatusRef.current = 'idle';
    setSyncMessage('');
    setPipelineStages([]);
    setProgressPct(0);
  }, []);

  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) eventSourceRef.current.close();
  }, []);

  return {
    syncStatus, syncMessage, setSyncMessage,
    previewOnly, setPreviewOnly,
    pipelineStages, currentStage, totalStages, progressPct,
    lastRunId, lastRunDryRun, lastRun,
    tokenPreview, tokenPreviewOpen, setTokenPreviewOpen,
    triggerSync, resetSync, closeEventSource,
  };
}
